import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account, Transaction, TransferTemplate, UserAccount } from "model";
/**
* @class TransferTransaction
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.checking[0];
* const transaction = new TransferTransaction(record);
* @property {NUMBER} _mapID - The id of the transfer to relate to the transaction
* @property {account} _from - account transferred from
* @property {account} _to - account transferred to
* @property {STRING} _options - option selected for transfer
* @property {STRING} _transferType - the type of transfer
* @property {STRING} _frequency - how often the transfer repeats
* @property {bcmsDate} _onDate - number of days in future the transfer will be made
* @property {STRING} _duration - when should transfer stop repeating
* @property {NUMBER} _times - how many times should transfer repeat. 0 for a one time payment
* @property {bcmsDate} _repeatDate - number of days in the future when transfer will repeat 0 if non repeating transfer
* @property {STRING} _amountType - what type of amount was selected
* @property {STRING} _note - what to include in note field. if no notes then use n/a
* @property {BOOLEAN} _sendEmail - is the send email box checked for the transfer
* @property {BOOLEAN} _needsApproval - tells us if the transfer needs an approval
* @property {STRING} _levelOfApprover - tells us the level of the approver
* @property {STRING} _numberOfApprovals - tells us the number of approvals confirmed
* @property {STRING} _numberOfApprovalsNeeded - tells us the number of approvals needed
* @property {STRING} _schedule - tells us the schedule status like "Immediate (One Time)", "Future (One Time)", or "Recurring".
* @property {BCMSDate} _approveByDate - deadline for approval.
* @property {STRING} _createdBy - name of the person that created the transfer.
* @property {STRING} _numberOfTransfers - the number of transfers needed.
* @property {BCMSDate} _startDate - the start date of the transfer.
* @property {BCMSDate} _endDate - the end date of the transfer.
* @property {BCMSDate} _estimatedDeliveryDate - the estimated delivery date of the transfer.
* @property {BCMSDate} _sendOn - the date in the "Send On" field in the "Make a transfer" workflow.
* @property {STRING} _paymentOption - the payment option chosen like "Other Amount".
* @property {STRING} _firstTransferDay - the first transfer day chosen, if applicable.
* @property {STRING} _secondTransferDay - the second transfer day chosen, if applicable.
* @property {STRING} _template - the template chosen, if applicable.
*/
export default class TransferTransaction extends BCMSRecord{
    _mapID;   
    _from;  
    _to;   
    _options;
    _transferType   
    _frequency;
    _onDate; 
    _duration;  
    _times;   
    _repeatDate;
    _amountType
    _note   
    _sendEmail; 
    _needsApproval;
    _levelOfApprover;
    _numberOfApprovals;
    _numberOfApprovalsNeeded;
    _schedule;
    _approveByDate;
    _createdBy;
    _numberOfTransfers;
    _startDate;
    _endDate;
    _estimatedDeliveryDate;
    _sendOn;
    _paymentOption;
    _firstTransferDay;
    _secondTransferDay;
    _template;

    constructor (record){  
      if(record.mapID){
        super(Object.assign(record,Transaction.initByID(record.mapID).record))  
      }else{
        super(Object.assign(record,Transaction.init(record).record))   
      } 
              
    } 
      /**   
      * @static    
      * @memberof TransferTransaction 
      */   

    static getPendingApprovalTableScheduleMessage(transferTransaction) {
      // Code below is unused for now since video shows 1 type of message, 
      // but this code may be needed later for full cert support.
      let message = "One time";

      if(transferTransaction.schedule) {
        const schedule = transferTransaction.schedule.toLowerCase();

        if(schedule.includes("one time")) {
          message = "One time";
        } else {
          message = transferTransaction.frequency;
        }
      }
      return message;
    }

    static getPendingApprovalTableDetailsScheduleMessage(transferTransaction) {
      return `Once on ${ transferTransaction.onDate.format("m/d/Y") }`;
      // Code below is unused for now since video shows 1 type of message, 
      // but this code may be needed later for full cert support.

      /*const schedule = transferTransaction.schedule.toLowerCase();
      const frequency = transferTransaction.frequency.toLowerCase();
      const duration = transferTransaction.duration.toLowerCase();
      const numberOfTransfers = transferTransaction.numberOfTransfers;

      const frequencyMessage = {
        "daily": `Every day`,
        "weekly": `Every week`,
        "every two weeks": `Every two weeks`,
        "twice a month": `Twice a month`,
        "every four weeks": `Every four weeks`,
        "monthly": `Every month`,
        "every two months": `Every 2 months`,
        "quarterly": `Every 3 months`,
        "every six months": `Every 6 months`,
        "annually": `Every year starting`
      };

      let startDateMessage = `, starting ${ transferTransaction.startDate.format("m/d/Y") }`;

      if(frequencyMessage[frequency] === `Every year starting`) {
        startDateMessage = ` on ${ transferTransaction.startDate.format("F j") }, starting ${ transferTransaction.startDate.format("m/d/Y") }`
      }

      const durationMessage = {
        "no end date": ``,
        "end by a specified date": ` and ending on ${ transferTransaction.endDate.format("m/d/Y") }`,
        "end after a specified number of transfers": ` and ending after ${ numberOfTransfers } occurrences`
      };

      let message = `${ frequencyMessage[frequency] }${ startDateMessage }${ durationMessage[duration] }`;

      if(schedule.includes("one time")) {
        return `Once on ${ transferTransaction.onDate.format("m/d/Y") }`;
      } else {
        return message;
      }*/
    }
   
  } 
  TransferTransaction.define({
    "recordType":"transferTransactions",
    "recordIDProp":"mapID",
    "className" : "TransferTransaction",
    "recordProperties":[
      /** 
      * @name mapID
      * @instance
      * @property get {NUMBER} - gets the mapID of the transfer
      * @property set {NUMBER} - sets the mapID of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToNumber("mapID","_mapID"),
      /** 
      * @name from
      * @instance
      * @property get {Account} - gets the from of the transfer
      * @property set {Account} - sets the from of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToClass("from","_from",()=>Account),
      /** 
      * @name to
      * @instance
      * @property get {Account} - gets the to of the transfer
      * @property set {Account} - sets the to of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToClass("to","_to",()=>Account),
      /** 
      * @name options
      * @instance
      * @property get {STRING} - gets the options of the transfer
      * @property set {STRING} - sets the options of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToString("options","_options"),
      /** 
      * @name transferType
      * @instance
      * @property get {NUMBER} - gets the transferType of the transfer
      * @property set {NUMBER} - sets the transferType of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToNumber("transferType","_transferType"),
      /** 
       * @name frequency
       * @instance
       * @property get {STRING} - gets the frequency of the transfer
       * @property set {STRING} - sets the frequency of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("frequency","_frequency"),
      /** 
       * @name onDate
       * @instance
       * @property get {BCMSDate} - gets the onDate of the transfer
       * @property set {BCMSDate} - sets the onDate of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToDate("onDate","_onDate"),
      /** 
       * @name duration
       * @instance
       * @property get {STRING} - gets the duration of the transfer
       * @property set {STRING} - sets the duration of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("duration","_duration"),          
      /** 
      * @name times
      * @instance
      * @property get {NUMBER} - gets the times of the transfer
      * @property set {NUMBER} - sets the times of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToNumber("times","_times"),
      /** 
      * @name repeatDate
      * @instance
      * @property get {BCMSDate} - gets the repeatDate of the transfer
      * @property set {BCMSDate} - sets the repeatDate of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToDate("repeatDate","_repeatDate"),
      /** 
      * @name amountType
      * @instance
      * @property get {STRING} - gets the amountType of the transfer
      * @property set {STRING} - sets the amountType of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToString("amountType","_amountType"),
      /** 
      * @name note
      * @instance
      * @property get {STRING} - gets the note of the transfer
      * @property set {STRING} - sets the note of the transfer
      * @memberof TransferTransaction 
      */
      BCMSRecordProperty.mapToString("note","_note"),    
      /** 
       * @name sendEmail
       * @instance
       * @property get {STRING} - gets the sendEmail of the transfer
       * @property set {STRING} - sets the sendEmail of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("sendEmail","_sendEmail"),

      /** 
       * @name needsApproval
       * @instance
       * @property get {BOOLEAN} - gets the needsApproval of the transfer
       * @property set {BOOLEAN} - sets the needsApproval of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToBoolean("needsApproval", "_needsApproval"),

      /** 
       * @name levelOfApprover
       * @instance
       * @property get {STRING} - gets the levelOfApprover of the transfer
       * @property set {STRING} - sets the levelOfApprover of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("levelOfApprover", "_levelOfApprover"),

      /** 
       * @name numberOfApprovals
       * @instance
       * @property get {STRING} - gets the numberOfApprovals of the transfer
       * @property set {STRING} - sets the numberOfApprovals of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("numberOfApprovals", "_numberOfApprovals"),

      /** 
       * @name numberOfApprovalsNeeded
       * @instance
       * @property get {STRING} - gets the numberOfApprovalsNeeded of the transfer
       * @property set {STRING} - sets the numberOfApprovalsNeeded of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("numberOfApprovalsNeeded", "_numberOfApprovalsNeeded"),

      /** 
       * @name schedule
       * @instance
       * @property get {STRING} - gets the schedule of the transfer
       * @property set {STRING} - sets the schedule of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("schedule", "_schedule"),

      /** 
       * @name approveByDate
       * @instance
       * @property get {STRING} - gets the schedule of the transfer
       * @property set {STRING} - sets the schedule of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToDate("approveByDate", "_approveByDate"),

      /** 
       * @name createdBy
       * @instance
       * @property get {STRING} - gets the createdBy of the transfer
       * @property set {STRING} - sets the createdBy of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToClass("createdBy", "_createdBy", () => UserAccount),

      /** 
       * @name numberOfTransfers
       * @instance
       * @property get {STRING} - gets the createdBy of the transfer
       * @property set {STRING} - sets the createdBy of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("numberOfTransfers", "_numberOfTransfers"),

      /** 
       * @name startDate
       * @instance
       * @property get {STRING} - gets the startDate of the transfer
       * @property set {STRING} - sets the startDate of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToDate("startDate", "_startDate"),

      /** 
       * @name endDate
       * @instance
       * @property get {STRING} - gets the endDate of the transfer
       * @property set {STRING} - sets the endDate of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToDate("endDate", "_endDate"),

      /** 
       * @name estimatedDeliveryDate
       * @instance
       * @property get {STRING} - gets the estimatedDeliveryDate of the transfer
       * @property set {STRING} - sets the estimatedDeliveryDate of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToDate("estimatedDeliveryDate", "_estimatedDeliveryDate"),

      /** 
       * @name sendOn
       * @instance
       * @property get {STRING} - gets the sendOn value of the transfer
       * @property set {STRING} - sets the sendOn value of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToDate("sendOn", "_sendOn"),

      /** 
       * @name paymentOption
       * @instance
       * @property get {STRING} - gets the paymentOption value of the transfer
       * @property set {STRING} - sets the paymentOption value of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("paymentOption", "_paymentOption"),

      /** 
       * @name firstTransferDay
       * @instance
       * @property get {STRING} - gets the firstTransferDay value of the transfer
       * @property set {STRING} - sets the firstTransferDay value of the transfer
       * @memberof TransferTransaction 
       */
      BCMSRecordProperty.mapToString("firstTransferDay", "_firstTransferDay"),

      /** 
       * @name secondTransferDay
       * @instance
       * @property get {STRING} - gets the secondTransferDay value of the transfer
       * @property set {STRING} - sets the secondTransferDay value of the transfer
       * @memberof TransferTransaction
       */
      BCMSRecordProperty.mapToString("secondTransferDay", "_secondTransferDay"),

      /** 
       * @name template
       * @instance
       * @property get {STRING} - gets the template value of the transfer
       * @property set {STRING} - sets the template value of the transfer
       * @memberof TransferTransaction
       */
      BCMSRecordProperty.mapToClass("template", "_template", () => TransferTemplate),

    ].concat(Transaction._RecordProperties)
})




